import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import { API_ORIGIN } from "../api";
import { useAuth } from "../context/AuthContext";
import AdminLayout from "../components/admin/AdminLayout";
import PrimaryActionButton from "../components/admin/PrimaryActionButton";
import DataTable from "../components/admin/DataTable";
import RichTextBlock from "../components/admin/RichTextBlock";
import {
  EDITIONS,
  NEWS_CATEGORIES,
  editionSourceLabel,
  newsCategoryLabel
} from "../config/adminConfigs";
import { escapeText, hasRichMarkup, richTextToHtml, sanitizeRichText, unescapeText } from "../utils/richText";

// ─── helpers ────────────────────────────────────────────────────────────────

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
};

const formatDateLong = (value) => {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const h = dt.getHours() % 12 || 12;
  const m = String(dt.getMinutes()).padStart(2, "0");
  const ampm = dt.getHours() < 12 ? "AM" : "PM";
  return `${dt.getDate()} ${months[dt.getMonth()]}, ${dt.getFullYear()}  ${h}:${m} ${ampm}`;
};

// <input type="datetime-local"> works in the publisher's local time with no
// timezone, as "YYYY-MM-DDTHH:mm". These convert to and from the UTC instant
// the server stores.
const pad2 = (n) => String(n).padStart(2, "0");

const toLocalInputValue = (value) => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const BROWSER_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
})();

// Build the content string from blocks — text blocks joined with \n\n, image blocks become [IMAGE_N].
//
// A text block's `text` is always in the editor's escaped form (see
// utils/richText.js). If no block carries any formatting the article is saved
// as plain text exactly as before; otherwise the escaped/tagged form is kept.
const blocksToContent = (blocks) => {
  const rich = blocks.some((b) => b.type === "text" && hasRichMarkup(b.text));
  let imageIndex = 0;
  return blocks
    .map((b) => {
      if (b.type === "image") {
        return `[IMAGE_${imageIndex++}]`;
      }
      const text = b.text.trim();
      return rich ? text : unescapeText(text);
    })
    .filter(Boolean)
    .join("\n\n");
};

// Parse existing content + inlineImages back into blocks for editing
const contentToBlocks = (content, existingInlineImages) => {
  const existing = Array.isArray(existingInlineImages) ? existingInlineImages : [];
  if (!content && existing.length === 0) {
    return [{ id: Date.now(), type: "text", text: "" }];
  }
  if (!content) {
    return [
      { id: Date.now(), type: "text", text: "" },
      ...existing.map((path, i) => ({
        id: Date.now() + i + 1,
        type: "image",
        file: null,
        preview: path ? `${API_ORIGIN}${path}` : null,
        existing: path,
      })),
    ];
  }

  // Text blocks hold the escaped form: stored rich content already is, legacy
  // plain text is escaped here and un-escaped again on save if left unformatted.
  const rich = hasRichMarkup(content);
  const toBlockText = (part) => (rich ? sanitizeRichText(part.trim()) : escapeText(part.trim()));

  // Split on [IMAGE_N] markers, keeping the markers
  const parts = content.split(/(\[IMAGE_\d+\])/);
  const blocks = [];
  let imageIndex = 0;
  parts.forEach((part, i) => {
    const markerMatch = part.match(/^\[IMAGE_(\d+)\]$/);
    if (markerMatch) {
      const idx = parseInt(markerMatch[1], 10);
      const path = existing[idx] || null;
      blocks.push({
        id: Date.now() + i,
        type: "image",
        file: null,
        preview: path ? `${API_ORIGIN}${path}` : null,
        existing: path,
        imageIndex: imageIndex++,
      });
    } else if (part.trim()) {
      blocks.push({ id: Date.now() + i, type: "text", text: toBlockText(part) });
    }
  });

  if (blocks.length === 0) {
    blocks.push({ id: Date.now(), type: "text", text: "" });
  }
  return blocks;
};

// ─── ArticlePreview ──────────────────────────────────────────────────────────

const ArticlePreview = ({ form, blocks, headerPreview }) => {
  const sourceLabel = editionSourceLabel(form.edition);
  const authorName = (form.author || "").trim();

  const renderBlocks = () =>
    blocks.map((block) => {
      if (block.type === "image") {
        const src = block.preview || (block.existing ? `${API_ORIGIN}${block.existing}` : null);
        if (!src) return null;
        return (
          <img
            key={block.id}
            src={src}
            alt="inline"
            style={{ width: "100%", borderRadius: 8, margin: "16px 0", display: "block" }}
          />
        );
      }
      // text block — split on double newlines into paragraphs. Block text is in
      // the editor's escaped form, so it is safe to render once sanitised.
      return block.text
        .split(/\n\s*\n/)
        .filter((p) => p.trim())
        .map((para, i) => (
          <p
            key={`${block.id}-${i}`}
            style={{ margin: "0 0 14px", lineHeight: 1.65, fontSize: 15, whiteSpace: "pre-line" }}
            dangerouslySetInnerHTML={{ __html: richTextToHtml(para.trim()) }}
          />
        ));
    });

  return (
    <div className="article-preview-panel">
      <div className="article-preview-label">Live Preview</div>

      {/* Header image */}
      {headerPreview?.length > 0 ? (
        <div className="article-preview-header-gallery">
          {headerPreview.map((src, index) => (
            <div key={`${src}-${index}`} className="article-preview-header-img">
              <img src={src} alt={`header ${index + 1}`} />
            </div>
          ))}
        </div>
      ) : (
        <div className="article-preview-header-placeholder">Header Image</div>
      )}

      {/* Meta */}
      <div className="article-preview-body">
        <div className="article-preview-meta">
          {form.edition && <span className="article-preview-edition">{form.edition}</span>}
          {form.edition && <span className="article-preview-meta-sep">|</span>}
          <span>{formatDateLong(new Date().toISOString())}</span>
        </div>

        {/* Attribution — source is derived from the edition, author is typed in */}
        {(sourceLabel || authorName) && (
          <div className="article-preview-meta">
            {sourceLabel && <span>Source: {sourceLabel}</span>}
            {sourceLabel && authorName && <span className="article-preview-meta-sep">|</span>}
            {authorName && <span>By: {authorName}</span>}
          </div>
        )}

        {/* Title */}
        {form.title && (
          <h2 className="article-preview-title">{form.title}</h2>
        )}

        <hr className="article-preview-divider" />

        {/* Content blocks — the body is the only article text now, so this
            preview matches what a reader sees in the app one-for-one. */}
        <div className="article-preview-content">{renderBlocks()}</div>
      </div>
    </div>
  );
};

// ─── BlockEditor ─────────────────────────────────────────────────────────────

const BlockEditor = ({ blocks, onChange }) => {
  const fileInputRefs = useRef({});

  const addTextBlock = (afterId) => {
    const newBlock = { id: Date.now(), type: "text", text: "" };
    const idx = blocks.findIndex((b) => b.id === afterId);
    const next = [...blocks];
    next.splice(idx + 1, 0, newBlock);
    onChange(next);
  };

  const addImageBlock = (afterId) => {
    const newBlock = { id: Date.now(), type: "image", file: null, preview: null, existing: null };
    const idx = blocks.findIndex((b) => b.id === afterId);
    const next = [...blocks];
    next.splice(idx + 1, 0, newBlock);
    onChange(next);
  };

  const removeBlock = (id) => {
    const next = blocks.filter((b) => b.id !== id);
    if (next.length === 0) next.push({ id: Date.now(), type: "text", text: "" });
    onChange(next);
  };

  // Functional update: the rich editor emits on every keystroke, and its
  // callback must not close over a stale `blocks`.
  const updateText = (id, text) => {
    onChange((current) => current.map((b) => (b.id === id ? { ...b, text } : b)));
  };

  const handleImageFile = (id, file) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    onChange(blocks.map((b) => (b.id === id ? { ...b, file, preview, existing: null } : b)));
  };

  const moveBlock = (id, dir) => {
    const idx = blocks.findIndex((b) => b.id === id);
    const next = [...blocks];
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    onChange(next);
  };

  return (
    <div className="block-editor">
      {blocks.map((block, idx) => (
        <div key={block.id} className={`block-item block-item--${block.type}`}>
          {/* Block controls */}
          <div className="block-controls">
            <button type="button" className="block-ctrl-btn" title="Move up" onClick={() => moveBlock(block.id, "up")} disabled={idx === 0}>↑</button>
            <button type="button" className="block-ctrl-btn" title="Move down" onClick={() => moveBlock(block.id, "down")} disabled={idx === blocks.length - 1}>↓</button>
            <button type="button" className="block-ctrl-btn block-ctrl-btn--danger" title="Remove block" onClick={() => removeBlock(block.id)}>✕</button>
          </div>

          {/* Block content */}
          {block.type === "text" ? (
            <RichTextBlock
              initialText={block.text}
              placeholder="Write or paste paragraph text here…"
              onChange={(text) => updateText(block.id, text)}
            />
          ) : (
            <div className="block-image-slot">
              {block.preview || block.existing ? (
                <div className="block-image-preview-wrap">
                  <img
                    src={block.preview || (block.existing ? `${API_ORIGIN}${block.existing}` : "")}
                    alt="block"
                    className="block-image-preview"
                  />
                  <button
                    type="button"
                    className="block-image-replace-btn"
                    onClick={() => fileInputRefs.current[block.id]?.click()}
                  >
                    Replace image
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="block-image-upload-btn"
                  onClick={() => fileInputRefs.current[block.id]?.click()}
                >
                  📷 Click to upload image
                </button>
              )}
              <input
                ref={(el) => { fileInputRefs.current[block.id] = el; }}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleImageFile(block.id, e.target.files?.[0])}
              />
            </div>
          )}

          {/* Add block buttons */}
          <div className="block-add-row">
            <button type="button" className="block-add-btn" onClick={() => addTextBlock(block.id)}>+ Text</button>
            <button type="button" className="block-add-btn block-add-btn--image" onClick={() => addImageBlock(block.id)}>+ Image</button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── NewsAdminPage ────────────────────────────────────────────────────────────

const initialMeta = {
  title: "",
  edition: "",
  author: "",
  category: "top",
  tags: "",
  status: "draft",
  // Local date and time for a scheduled article ("YYYY-MM-DDTHH:mm"), only used
  // while status is "scheduled".
  scheduledFor: "",
  headerImages: [],
  headerPreviews: [],
};

const NewsAdminPage = ({ module }) => {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [meta, setMeta] = useState({ ...initialMeta, edition: module.defaultEdition || "" });
  const [blocks, setBlocks] = useState([{ id: Date.now(), type: "text", text: "" }]);
  const [filterEdition, setFilterEdition] = useState(module.defaultEdition || "");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [publishingAll, setPublishingAll] = useState(false);
  // Whether Publish All sends a push notification per article. On by default,
  // the same as publishing one article by hand.
  const [notifyOnPublishAll, setNotifyOnPublishAll] = useState(true);
  // Managed categories of every edition (Categories page), so a newly added
  // one can be picked here. Until they load, or if loading fails, the built-in
  // list is used for every edition.
  const [allCategories, setAllCategories] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get("/categories")
      .then(({ data }) => {
        if (active && Array.isArray(data.categories) && data.categories.length > 0) {
          setAllCategories(data.categories);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Categories are per edition: the options for one edition, or (for the
  // "All editions" filter) every slug once.
  const categoriesFor = useCallback(
    (edition) => {
      if (!allCategories) {
        return NEWS_CATEGORIES.map((slug) => ({ slug, name: newsCategoryLabel(slug), is_active: true }));
      }
      if (edition) return allCategories.filter((c) => c.edition === edition);
      const seen = new Set();
      return allCategories
        .filter((c) => (seen.has(c.slug) ? false : seen.add(c.slug)))
        .map((c) => ({ ...c, is_active: true }));
    },
    [allCategories]
  );
  const categoryOptions = categoriesFor(meta.edition);
  const filterCategoryOptions = categoriesFor(filterEdition);

  const categoryOptionLabel = (category) =>
    `${category.name || newsCategoryLabel(category.slug)}${category.is_active ? "" : " (hidden in app)"}`;

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterEdition) params.edition = filterEdition;
      if (filterCategory) params.category = filterCategory;
      if (filterStatus) params.status = filterStatus;
      const response = await api.get("/news", { params });
      setNews(response.data.news || []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Could not load news");
    } finally {
      setLoading(false);
    }
  }, [filterEdition, filterCategory, filterStatus]);

  useEffect(() => { fetchNews(); }, [fetchNews]);

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setMeta({ ...initialMeta, edition: module.defaultEdition || "" });
    setBlocks([{ id: Date.now(), type: "text", text: "" }]);
  };

  const onMetaInput = (e) => {
    const { name, value, files } = e.target;
    if (name === "headerImage") {
      const selectedFiles = Array.from(files || []);
      setMeta((prev) => ({
        ...prev,
        headerImages: selectedFiles,
        headerPreviews: selectedFiles.map((file) => URL.createObjectURL(file)),
      }));
      return;
    }
    setMeta((prev) => ({ ...prev, [name]: value }));
  };

  const submitNews = async (e) => {
    e.preventDefault();
    setError("");

    // A scheduled article needs a valid time. A time that is already saved on
    // the article may be kept as it is, even if it has just passed.
    let scheduledIso = "";
    if (meta.status === "scheduled") {
      const when = new Date(meta.scheduledFor);
      if (!meta.scheduledFor || Number.isNaN(when.getTime())) {
        setError("Choose the date and time to publish.");
        return;
      }
      const saved = editingId ? news.find((n) => n._id === editingId)?.scheduledFor : null;
      const keepsSavedTime = saved && new Date(saved).getTime() === when.getTime();
      if (!keepsSavedTime && when.getTime() <= Date.now()) {
        setError("Choose a publish time in the future.");
        return;
      }
      scheduledIso = when.toISOString();
    }

    // Build content string from blocks
    const content = blocksToContent(blocks);

    // Send each image block's file as inlineImage_N
    const imageBlocks = blocks.filter((b) => b.type === "image");

    const payload = new FormData();
    payload.append("title", meta.title);
    // `description` is intentionally not sent. Omitting it (rather than sending
    // "") also means updateNews leaves any existing stored lead untouched, so
    // editing an older article doesn't wipe its standfirst.
    payload.append("content", content);
    payload.append("edition", meta.edition);
    // `source` is intentionally not sent — the server derives it from `edition`
    // so the two can never drift apart.
    payload.append("author", meta.author.trim());
    payload.append("category", meta.category);
    payload.append("tags", meta.tags);
    payload.append("status", meta.status);
    if (scheduledIso) payload.append("scheduledFor", scheduledIso);
    if (meta.headerImages.length > 0) {
      meta.headerImages.forEach((file, index) => {
        payload.append(`headerImage_${index}`, file);
      });
    }
    imageBlocks.forEach((block, i) => {
      if (block.file) payload.append(`inlineImage_${i}`, block.file);
    });

    try {
      if (editingId) {
        await api.put(`/news/${editingId}`, payload);
      } else {
        await api.post("/news", payload);
      }
      await fetchNews();
      closeModal();
      setNotice(
        scheduledIso
          ? `Article scheduled. It will be published on ${formatDateLong(scheduledIso)}.`
          : ""
      );
    } catch (err) {
      setError(err.response?.data?.message || "Could not save news");
    }
  };

  const openEdit = (item) => {
    setEditingId(item._id);
    setMeta({
      title: item.title || "",
      edition: item.edition || module.defaultEdition || "",
      // Older articles have no author stored — start blank so the admin can fill it in.
      author: item.author || "",
      category: item.category || "top",
      tags: (item.tags || []).join(", "),
      status: item.status || "draft",
      scheduledFor: item.scheduledFor ? toLocalInputValue(item.scheduledFor) : "",
      headerImages: [],
      headerPreviews: Array.isArray(item.headerImages) && item.headerImages.length > 0
        ? item.headerImages.map((path) => `${API_ORIGIN}${path}`)
        : item.imagePath
          ? [`${API_ORIGIN}${item.imagePath}`]
          : [],
    });
    setBlocks(contentToBlocks(item.content || "", item.inlineImages || []));
    setModalOpen(true);
  };

  const openPreview = async (id) => {
    try {
      const response = await api.get(`/news/${id}`);
      setPreviewItem(response.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Could not load preview");
    }
  };

  const deleteNews = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await api.delete(`/news/${id}`);
      await fetchNews();
    } catch (err) {
      setError(err.response?.data?.message || "Could not delete article");
    }
  };

  const publishNews = async (id) => {
    try {
      await api.patch(`/news/${id}/publish`);
      await fetchNews();
      setNotice("News published successfully.");
    } catch (err) {
      setError(err.response?.data?.message || "Could not publish article");
    }
  };

  // Publishes every draft in the list currently loaded (i.e. matching the
  // Edition / Category / Status filters above the table) in one request. The
  // server rebuilds the published JSON feeds once for the whole batch instead of
  // once per article.
  // Drafts only: scheduled articles publish themselves at their own time.
  const draftIds = useMemo(
    () => news.filter((item) => item.status === "draft").map((item) => item._id),
    [news]
  );

  // A published article can't be moved back to "scheduled".
  const editingWasPublished = useMemo(
    () => !!editingId && news.find((n) => n._id === editingId)?.status === "published",
    [editingId, news]
  );

  const publishAll = async () => {
    if (draftIds.length === 0 || publishingAll) return;
    const scope = [filterEdition, filterCategory && newsCategoryLabel(filterCategory)].filter(Boolean).join(" / ");
    const confirmed = window.confirm(
      `Publish all ${draftIds.length} draft article${draftIds.length === 1 ? "" : "s"}` +
        `${scope ? ` in ${scope}` : ""}?\n\n` +
        (notifyOnPublishAll
          ? `A push notification will be sent to readers for each article (${draftIds.length} in total).`
          : "No push notifications will be sent.")
    );
    if (!confirmed) return;

    setPublishingAll(true);
    setError("");
    setNotice("");
    try {
      const response = await api.post("/news/publish-all", {
        ids: draftIds,
        notify: notifyOnPublishAll
      });
      const { published = 0, failed = 0 } = response.data || {};
      await fetchNews();
      if (failed > 0) {
        setError(`${failed} article${failed === 1 ? "" : "s"} could not be published. Please try again.`);
      }
      setNotice(`${published} article${published === 1 ? "" : "s"} published successfully.`);
    } catch (err) {
      setError(err.response?.data?.message || "Could not publish articles");
    } finally {
      setPublishingAll(false);
    }
  };

  const columns = useMemo(
    () => [
      { key: "index", label: "#", accessor: () => "", searchable: false, render: (_row, index) => index },
      { key: "date", label: "News Date", accessor: "createdAt", sortAccessor: (row) => row.createdAt || "", render: (row) => formatDate(row.createdAt) },
      {
        key: "image", label: "Header Image", accessor: "imagePath", searchable: false,
        render: (row) => row.imagePath
          ? <img src={`${API_ORIGIN}${row.imagePath}`} alt={row.title} className="table-news-thumb" />
          : "—"
      },
      { key: "title", label: "Gujarati/Hindi Title", accessor: (row) => row.localized?.gu?.title || row.localized?.hi?.title || row.title || "—" },
      { key: "type", label: "Type", accessor: (row) => row.edition || "—" },
      { key: "category", label: "Category", accessor: (row) => row.category || "—" },
      {
        key: "publishStatus", label: "Publish Status", accessor: (row) => row.status || "draft",
        render: (row) => (
          <span className={`publish-label ${row.status === "published" ? "published" : row.status === "scheduled" ? "scheduled" : "draft"}`}>
            {row.status === "published" ? "Published" : row.status === "scheduled" ? "Scheduled" : "Draft"}
            {row.status === "scheduled" && row.scheduledFor && (
              <span className="publish-when">{formatDateLong(row.scheduledFor)}</span>
            )}
          </span>
        )
      },
      {
        key: "actions", label: "Actions", accessor: () => "", sortable: false, searchable: false,
        render: (row) => (
          <div className="table-actions">
            <button type="button" className="icon-btn" onClick={() => openPreview(row._id)} title="View">👁</button>
            <button type="button" className="icon-btn" onClick={() => openEdit(row)} title="Edit">✏️</button>
            {row.status !== "published" && (
              <button
                type="button"
                className="icon-btn publish-btn-mini"
                onClick={() => publishNews(row._id)}
                title={row.status === "scheduled" ? "Publish now (cancels the schedule)" : "Publish"}
              >↑</button>
            )}
            {user?.role === "admin" && (
              <button type="button" className="delete-btn" onClick={() => deleteNews(row._id)} title="Delete">✖</button>
            )}
          </div>
        )
      }
    ],
    [user?.role]
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AdminLayout module={module} title={module.pageTitle}>
      {error && <p className="error-banner">{error}</p>}
      {notice && <p className="success-banner">{notice}</p>}

      <div className="toolbar-row">
        <div className="toolbar-actions">
          <PrimaryActionButton
            label={module.primaryActionLabel}
            onClick={() => {
              setEditingId(null);
              setMeta({ ...initialMeta, edition: module.defaultEdition || "" });
              setBlocks([{ id: Date.now(), type: "text", text: "" }]);
              setModalOpen(true);
            }}
          />
          <button
            type="button"
            className="publish-all-btn"
            onClick={publishAll}
            disabled={loading || publishingAll || draftIds.length === 0}
            title={
              draftIds.length === 0
                ? "No draft articles in the current list"
                : "Publish every draft in the current list at once"
            }
          >
            {publishingAll ? "Publishing…" : `Publish All${draftIds.length ? ` (${draftIds.length})` : ""}`}
          </button>
          <label className="publish-all-notify" title="Untick to publish without sending push notifications">
            <input
              type="checkbox"
              checked={notifyOnPublishAll}
              onChange={(e) => setNotifyOnPublishAll(e.target.checked)}
              disabled={publishingAll}
            />
            Notify readers
          </label>
        </div>
        <div className="quick-filters">
          <label>
            Edition
            <select value={filterEdition} onChange={(e) => setFilterEdition(e.target.value)}>
              <option value="">All</option>
              {EDITIONS.map((ed) => <option key={ed} value={ed}>{ed}</option>)}
            </select>
          </label>
          <label>
            Status
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
            </select>
          </label>
          <label>
            Category
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="">All</option>
              {filterCategoryOptions.map((c) => <option key={c.slug} value={c.slug}>{categoryOptionLabel(c)}</option>)}
            </select>
          </label>
        </div>
      </div>

      {loading ? <p className="loading-text">Loading news...</p> : <DataTable columns={columns} rows={news} />}

      {/* ── Create / Edit modal ── */}
      {isModalOpen && (
        <div className="modal-overlay news-editor-overlay">
          <div className="news-editor-shell">

            {/* Left: form fields */}
            <form className="news-editor-form" onSubmit={submitNews}>
              <div className="news-editor-form-header">
                <h2>{editingId ? "Edit Article" : "New Article"}</h2>
                <button type="button" className="ghost-btn" onClick={closeModal}>✕ Close</button>
              </div>

              <div className="news-editor-form-body">
              {error && <p className="error-banner">{error}</p>}

              {/* Meta fields */}
              <div className="news-meta-grid">
                <div>
                  <label htmlFor="ne-edition">Edition *</label>
                  <select id="ne-edition" name="edition" value={meta.edition} onChange={onMetaInput} required>
                    <option value="">Select edition</option>
                    {EDITIONS.map((ed) => <option key={ed} value={ed}>{ed}</option>)}
                  </select>
                </div>
                <div>
                  {/* Read-only: derived from the edition above, never typed in.
                      The server re-derives it on save, so this is display only. */}
                  <label htmlFor="ne-source">Source</label>
                  <input
                    id="ne-source"
                    name="source"
                    value={editionSourceLabel(meta.edition)}
                    readOnly
                    disabled
                    placeholder="Set automatically from edition"
                    title="Automatically set from the selected edition"
                  />
                </div>
                <div>
                  <label htmlFor="ne-author">Author</label>
                  <input
                    id="ne-author"
                    name="author"
                    value={meta.author}
                    onChange={onMetaInput}
                    placeholder="Author name"
                    maxLength={120}
                  />
                </div>
                <div>
                  <label htmlFor="ne-category">Category *</label>
                  <select id="ne-category" name="category" value={meta.category} onChange={onMetaInput} required>
                    {categoryOptions.map((c) => <option key={c.slug} value={c.slug}>{categoryOptionLabel(c)}</option>)}
                    {/* Keep the current category visible even if this edition
                        doesn't have it (e.g. the edition was just changed);
                        saving then asks for a category of this edition. */}
                    {meta.category && !categoryOptions.some((c) => c.slug === meta.category) && (
                      <option value={meta.category}>
                        {newsCategoryLabel(meta.category)}
                        {allCategories ? " (not in this edition)" : ""}
                      </option>
                    )}
                  </select>
                </div>
                <div>
                  <label htmlFor="ne-status">Status</label>
                  <select id="ne-status" name="status" value={meta.status} onChange={onMetaInput}>
                    <option value="draft">Draft</option>
                    <option value="scheduled" disabled={editingWasPublished}>Schedule for later</option>
                    <option value="published">Published</option>
                  </select>
                </div>
                {meta.status === "scheduled" && (
                  <div className="schedule-field">
                    <label htmlFor="ne-scheduled-for">Publish on *</label>
                    <input
                      id="ne-scheduled-for"
                      name="scheduledFor"
                      type="datetime-local"
                      value={meta.scheduledFor}
                      onChange={onMetaInput}
                      min={toLocalInputValue(new Date())}
                      required
                    />
                    <span className="schedule-hint">
                      The article goes live automatically at this time
                      {BROWSER_TIMEZONE ? ` (${BROWSER_TIMEZONE})` : ""} and readers are notified.
                    </span>
                  </div>
                )}
                <div>
                  <label htmlFor="ne-tags">Tags</label>
                  <input id="ne-tags" name="tags" value={meta.tags} onChange={onMetaInput} placeholder="comma separated" />
                </div>
              </div>

              <label htmlFor="ne-title">Title *</label>
              <input id="ne-title" name="title" value={meta.title} onChange={onMetaInput} required placeholder="Article headline" />

              {/* There is no "Description / Lead paragraph" field any more.
                  An article is a title plus a body. The lead used to be stored
                  and rendered as a separate block above the body in the app,
                  which repeatedly showed readers the same paragraph twice
                  whenever the lead restated the body's opening. Put the opening
                  paragraph at the top of the Article Body instead. */}

              {/* Header image */}
              <label>Header Image</label>
              <div className="header-image-upload-area">
                {meta.headerPreviews.length > 0 ? (
                  <div className="header-image-preview-wrap">
                    <div className="header-image-preview-grid">
                      {meta.headerPreviews.map((src, index) => (
                        <img key={`${src}-${index}`} src={src} alt={`header ${index + 1}`} className="header-image-preview" />
                      ))}
                    </div>
                    <label className="header-image-replace-btn">
                      Replace all
                      <input type="file" name="headerImage" accept="image/*" multiple onChange={onMetaInput} style={{ display: "none" }} />
                    </label>
                  </div>
                ) : (
                  <label className="header-image-upload-btn">
                    📷 Upload header images
                    <input type="file" name="headerImage" accept="image/*" multiple onChange={onMetaInput} style={{ display: "none" }} />
                  </label>
                )}
              </div>

              {/* Block editor */}
              <div className="block-editor-section">
                <div className="block-editor-section-label">
                  Article Body
                  <span className="block-editor-hint">Use "+ Text" and "+ Image" to build the article. Images appear inline between paragraphs.</span>
                </div>
                <BlockEditor blocks={blocks} onChange={setBlocks} />
              </div>

              <div className="modal-actions" style={{ marginTop: 16 }}>
                  <button type="submit" className="primary-action-btn">
                    {meta.status === "scheduled"
                      ? (editingId ? "Update Schedule" : "Schedule Article")
                      : (editingId ? "Update Article" : "Create Article")}
                  </button>
                  <button type="button" className="ghost-btn" onClick={closeModal}>Cancel</button>
                </div>
              </div>{/* end news-editor-form-body */}
            </form>

            {/* Right: live preview */}
            <div className="news-editor-preview-col">
              <ArticlePreview form={meta} blocks={blocks} headerPreview={meta.headerPreviews} />
            </div>
          </div>
        </div>
      )}

      {/* ── Read-only preview modal ── */}
      {previewItem && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: 520 }}>
            {previewItem.imagePath && (
              <img
                src={`${API_ORIGIN}${previewItem.imagePath}`}
                alt="header"
                style={{ width: "100%", borderRadius: 8, marginBottom: 12, display: "block" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            )}
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
              {previewItem.edition && <><strong style={{ color: "#2563eb" }}>{previewItem.edition}</strong> &nbsp;|&nbsp;</>}
              {formatDateLong(previewItem.publishedAt || previewItem.createdAt)}
            </div>
            {(() => {
              // Fall back to deriving the source from the edition for articles
              // saved before the field existed.
              const src = (previewItem.source || "").trim() || editionSourceLabel(previewItem.edition);
              const by = (previewItem.author || "").trim();
              if (!src && !by) return null;
              return (
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                  {src && <span>Source: {src}</span>}
                  {src && by && <>&nbsp;|&nbsp;</>}
                  {by && <span>By: {by}</span>}
                </div>
              );
            })()}
            <h2 style={{ margin: "0 0 8px", fontSize: 20, lineHeight: 1.3 }}>{previewItem.title}</h2>
            <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "10px 0" }} />
            {/* Guarded, not removed: new articles have no lead paragraph, but
                previously published ones still carry theirs and this is the
                only admin surface that shows it. Without the guard a blank
                styled paragraph left a gap at the top of every new article. */}
            {previewItem.description ? (
              <p style={{ margin: "0 0 12px", fontWeight: 500, lineHeight: 1.65 }}>{previewItem.description}</p>
            ) : null}
            {/* Render content blocks inline */}
            {previewItem.content && (() => {
              const inlineImgs = previewItem.inlineImages || [];
              const rich = hasRichMarkup(previewItem.content);
              let imgIdx = 0;
              return previewItem.content.split(/(\[IMAGE(?:_\d+)?\])/).map((part, i) => {
                const markerMatch = part.match(/^\[IMAGE(?:_(\d+))?\]$/);
                if (markerMatch) {
                  const idx = markerMatch[1] !== undefined ? parseInt(markerMatch[1], 10) : imgIdx;
                  imgIdx++;
                  const imgPath = inlineImgs[idx];
                  if (!imgPath) return null;
                  return (
                    <img
                      key={i}
                      src={`${API_ORIGIN}${imgPath}`}
                      alt="inline"
                      style={{ width: "100%", borderRadius: 8, margin: "12px 0", display: "block" }}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  );
                }
                return part.trim().split(/\n\s*\n/).filter(Boolean).map((para, j) =>
                  rich ? (
                    <p
                      key={`${i}-${j}`}
                      style={{ margin: "0 0 12px", lineHeight: 1.65, whiteSpace: "pre-line" }}
                      dangerouslySetInnerHTML={{ __html: richTextToHtml(para.trim()) }}
                    />
                  ) : (
                    <p key={`${i}-${j}`} style={{ margin: "0 0 12px", lineHeight: 1.65 }}>{para.trim()}</p>
                  )
                );
              });
            })()}
            <button type="button" className="ghost-btn" style={{ marginTop: 8 }} onClick={() => setPreviewItem(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default NewsAdminPage;
